// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sistema.notaria4otavalo.com.ec';
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || 'Error en la solicitud al servidor.';
    toast.error(message, { position: 'top-right', autoClose: 3000 });
    return Promise.reject(error);
  }
);

const NotarioDashboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [authorizations, setAuthorizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAuthorization, setSelectedAuthorization] = useState(null);
  const [action, setAction] = useState(null); // 'approve' o 'reject'
  const [notes, setNotes] = useState('');
  const [creditAlerts, setCreditAlerts] = useState(null);

  // Verificar permisos y redirigir si no es notario
  useEffect(() => {
    if (!loading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a esta página.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Obtener solicitudes de autorización pendientes
  useEffect(() => {
    const fetchAuthorizations = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/api/audit/edit-authorization/');
        setAuthorizations(response.data || []);
      } catch (error) {
        // Error manejado por el interceptor
      } finally {
        setIsLoading(false);
      }
    };

    if (!loading && user && user.is_notario) {
      fetchAuthorizations();
    }
  }, [user, loading]);

  useEffect(() => {
    const fetchCreditAlerts = async () => {
      try {
        const response = await api.get('/api/audit/credit-receivables/alerts/');
        setCreditAlerts(response.data || null);
      } catch (error) {
        setCreditAlerts(null);
      }
    };

    if (!loading && user && user.is_notario) {
      fetchCreditAlerts();
    }
  }, [user, loading]);

  const handleLogout = async () => {
    try {
      await api.post('/api/users/auth/logout/');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      toast.success('Sesión cerrada correctamente', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/login');
    } catch (error) {
      toast.error('Error al cerrar sesión', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      // En caso de error, aún redirigimos al login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const handleAuthorizationAction = async () => {
    if (!selectedAuthorization || !action) return;
    setIsLoading(true);
    try {
      setAuthorizations(authorizations.filter((auth) => auth.id !== selectedAuthorization.id));
      toast.success(`Solicitud ${action === 'approve' ? 'aprobada' : 'rechazadas'} exitosamente.`, {
        position: 'top-right',
        autoClose: 3000,
      });
      setSelectedAuthorization(null);
      setAction(null);
      setNotes('');
    } catch (error) {
      // Error manejado por el interceptor
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return <div className="text-white text-center">Cargando...</div>;
  }

  if (!user) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white bg-gray-900"
    >
      <header className="flex justify-between items-center p-6 bg-notaria-blue-1 bg-opacity-90 shadow-lg">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-16 h-auto mr-4" />
          <h1 className="text-3xl font-bold">Dashboard del Notario</h1>
        </div>
        <motion.button
          onClick={handleLogout}
          className="bg-notaria-red text-white font-bold py-2 px-6 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all duration-300 shadow-md"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Cerrar Sesión
        </motion.button>
      </header>
      <main className="flex-1 p-6 sm:p-10">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-6 sm:p-8 rounded-xl shadow-2xl mb-8"
        >
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4 sm:mb-6">Bienvenido, Dr. Zea</h2>
          <p className="mb-6 sm:mb-8 text-gray-200 text-sm sm:text-base">
            Desde este panel, puedes gestionar los trámites diarios, aprobar los depósitos realizados por el contador y configurar las comisiones.
          </p>
          {creditAlerts && (creditAlerts.overdue_count > 0 || creditAlerts.pending_approval_count > 0) && (
            <Link
              to="/credit-receivables"
              className="mb-6 block rounded-lg border border-notaria-yellow/50 bg-notaria-yellow/10 p-4 text-notaria-yellow hover:bg-notaria-yellow/20 transition"
            >
              <span className="block font-bold">Alertas de créditos</span>
              <span className="block text-sm text-gray-100">
                {creditAlerts.overdue_count} vencidos por ${Number(creditAlerts.overdue_total || 0).toFixed(2)} · {creditAlerts.pending_approval_count} por aprobar · {creditAlerts.approved_pending_count} aprobados por cobrar
              </span>
            </Link>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <Link
              to="/upload"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Cargar Trámites Diarios</span>
            </Link>
            <Link
              to="/approve"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Aprobar Depósitos</span>
            </Link>
            <Link
              to="/tramites"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Ver Trámites</span>
            </Link>
            <Link
              to="/configure-commission"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Configurar Comisión</span>
            </Link>
            <Link
              to="/verify-invoice-sequence"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Verificar Secuencia de Facturas</span>
            </Link>

            <Link
              to="/cash-extras"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Pagos de Extras (Efectivo)</span>
            </Link>
            <Link
              to="/financial-summary"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Resumen Financiero</span>
            </Link>
            <Link
              to="/credit-receivables"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Trámites por Cobrar</span>
            </Link>
            <Link
              to="/credit-receivables"
              className="relative bg-notaria-yellow text-notaria-blue-1 font-bold py-4 px-6 rounded-lg text-center hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
            >
              <span className="block text-sm sm:text-base font-semibold">Empresas y Responsables Crédito</span>
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-6xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-6 sm:p-8 rounded-xl shadow-2xl"
        >
          <h2 className="text-2xl sm:text-3xl font-semibold mb-6">Solicitudes de Autorización Pendientes</h2>
          {isLoading ? (
            <p className="text-center text-lg text-gray-300">Cargando solicitudes...</p>
          ) : authorizations.length === 0 ? (
            <p className="text-center text-lg text-gray-300">No hay solicitudes de autorización pendientes.</p>
          ) : (
            <div className="text-center">
              <p className="text-lg text-gray-200 mb-4">
                Tienes <span className="font-semibold text-notaria-yellow">{authorizations.length}</span> solicitud{authorizations.length !== 1 ? 'es' : ''} de autorización pendiente{authorizations.length !== 1 ? 's' : ''}.
              </p>
              <Link
                to="/manage-authorizations"
                className="inline-block bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-6 rounded-lg hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
              >
                Gestionar Solicitudes
              </Link>
            </div>
          )}
        </motion.div>
      </main>

      {selectedAuthorization && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-notaria-blue-1 bg-opacity-95 p-6 rounded-2xl shadow-2xl w-full max-w-md"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">
                {action === 'approve' ? 'Aprobar' : 'Rechazar'} Solicitud #{selectedAuthorization.id}
              </h3>
              <motion.button
                onClick={() => setSelectedAuthorization(null)}
                className="text-gray-300 hover:text-notaria-yellow transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Cerrar modal"
              >
                <XMarkIcon className="w-6 h-6" />
              </motion.button>
            </div>
            <div className="mb-4">
              <p><span className="font-semibold">Contador:</span> {selectedAuthorization.contador.username}</p>
              <p><span className="font-semibold">Trámites:</span> {selectedAuthorization.tramites.length}</p>
              <p><span className="font-semibold">Fecha:</span> {new Date(selectedAuthorization.fecha_solicitud).toLocaleString()}</p>
            </div>
            <div className="mb-4">
              <label htmlFor="notes" className="block text-sm font-semibold mb-1">
                Notas (Opcional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                rows="4"
              />
            </div>
            <div className="flex justify-end space-x-4">
              <motion.button
                onClick={() => setSelectedAuthorization(null)}
                className="bg-notaria-blue-2 text-white py-2 px-4 rounded-lg hover:bg-notaria-blue-3 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Cancelar"
              >
                Cancelar
              </motion.button>
              <motion.button
                onClick={handleAuthorizationAction}
                disabled={isLoading}
                className={`bg-${action === 'approve' ? 'notaria-green' : 'notaria-red'} text-white py-2 px-4 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                whileHover={{ scale: !isLoading ? 1.05 : 1 }}
                whileTap={{ scale: !isLoading ? 0.95 : 1 }}
                aria-label={action === 'approve' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
              >
                {isLoading ? 'Procesando...' : action === 'approve' ? 'Aprobar' : 'Rechazar'}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default NotarioDashboard;
